import { Link } from "react-router";
import { Database, Network, Gauge, Brain, PencilLine } from "lucide-react";
import { cn } from "@app/lib/utils";
import { Trans, msg, useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";

interface BentoItem {
  id: string;
  to: string | null;
  icon: React.ComponentType<{ className?: string }>;
  title: MessageDescriptor;
  description: MessageDescriptor;
  disabled?: boolean;
}

const bentoItems: BentoItem[] = [
  {
    id: "datasets",
    to: "/datasets",
    icon: Database,
    title: msg`Jeux de données`,
    description: msg`Construisez, annotez et évaluez vos collections de fichiers médicaux pour mesurer la qualité d'extraction.`,
  },
  {
    id: "ontologie",
    to: "/laboratoire/ontologie",
    icon: Network,
    title: msg`Ontologie`,
    description: msg`Définissez les champs cliniques à extraire, leurs règles déterministes et leur normalisation.`,
  },
  {
    id: "evaluateurs",
    to: "/laboratoire/metriques",
    icon: Gauge,
    title: msg`Évaluateurs / Métriques`,
    description: msg`Configurez les métriques de qualité et les évaluateurs pour scorer l'extraction de chaque champ.`,
  },
  {
    id: "corrections",
    to: "/laboratoire/corrections",
    icon: PencilLine,
    title: msg`Corrections`,
    description: msg`Lisez le journal des corrections par champ : ce que les médecins reprennent, dans quelle proportion, et pour quelle raison.`,
  },
  {
    id: "llm",
    to: "/laboratoire/llm",
    icon: Brain,
    title: msg`Modèle LLM`,
    description: msg`Configurez les modèles, paramètres d'inférence, le prompt système et la stratégie de sélection des exemples (few-shot).`,
  },
];

export default function LaboratoirePage() {
  const { i18n } = useLingui();

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">
          <Trans>Laboratoire</Trans>
        </h1>
        <p className="text-slate-600 mt-1">
          <Trans>
            Outils de recherche et d'expérimentation pour améliorer la qualité
            d'extraction
          </Trans>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {bentoItems.map((item) => {
          const Icon = item.icon;
          const isInteractive = !item.disabled && item.to != null;

          const cardClasses = cn(
            "group relative h-48 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm overflow-hidden transition-all duration-300",
            isInteractive
              ? "hover:shadow-lg hover:border-primary/50 hover:-translate-y-0.5 cursor-pointer"
              : "opacity-60 cursor-not-allowed",
          );

          const inner = (
            <>
              <div
                className={cn(
                  "absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300",
                  isInteractive && "group-hover:opacity-100",
                )}
                aria-hidden="true"
              />

              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      "flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors duration-300",
                      isInteractive && "group-hover:bg-primary group-hover:text-white",
                    )}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 flex-1">
                    {i18n._(item.title)}
                  </h2>
                  {item.disabled && (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-3xs font-medium text-slate-500 border border-slate-200">
                      <Trans>Bientôt disponible</Trans>
                    </span>
                  )}
                </div>

                <p
                  className={cn(
                    "mt-auto text-sm text-slate-600 transition-all duration-300",
                    isInteractive
                      ? "opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0"
                      : "opacity-100",
                  )}
                >
                  {i18n._(item.description)}
                </p>
              </div>
            </>
          );

          if (isInteractive && item.to) {
            return (
              <Link
                key={item.id}
                to={item.to}
                className={cardClasses}
                aria-label={i18n._(item.title)}
              >
                {inner}
              </Link>
            );
          }

          return (
            <div
              key={item.id}
              className={cardClasses}
              aria-disabled="true"
              role="presentation"
            >
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}
